/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ClipEnhancementInfo, PlaylistClip } from "types/Clip";
import { BlockTypes } from "constants/BlockTypes";
import { AudioEffect } from "types/AudioEffect";
import { Asset } from "types/Asset";
import { computeTwoSliderGains } from "components/EnhancedSpeech/computeTwoSliderGains";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { EnhancedLogger } from "utilities/Logger";

export class EnhanceV2MixEffect extends AudioEffect {
  private readonly mixNode: GainNode;
  private readonly originalGain: GainNode;
  private readonly enhancedGain: GainNode;
  private readonly backgroundGain: GainNode;
  private readonly isolatedSpeechGain: GainNode;
  private readonly splitter: ChannelSplitterNode;
  private readonly merger: ChannelMergerNode;

  static override providePermanentUrl(
    clip: PlaylistClip,
    asset: Asset
  ): string | undefined {
    if (!EnhanceV2MixEffect.canBuildFor(clip) || !asset.streamingAudioV2Url) {
      return undefined;
    }
    return asset.streamingAudioV2Url;
  }

  static override canUseLocalAsset(): boolean {
    return false;
  }

  static override canBuildFor(clip: PlaylistClip): clip is PlaylistClip & {
    type: BlockTypes.TRANSCRIPTION;
    enhancementInfo: ClipEnhancementInfo;
  } {
    return (
      clip?.type === BlockTypes.TRANSCRIPTION &&
      clip?.enhancementInfo !== undefined &&
      clip?.enhancementInfo?.enhanceSpeechModelVersion === ModelVersion.v2
    );
  }

  constructor(
    audioContext: AudioContext,
    clip: PlaylistClip,
    playlistTime: number,
    logger?: EnhancedLogger
  ) {
    if (!EnhanceV2MixEffect.canBuildFor(clip)) {
      throw Error(
        "Can't apply enhance v2 mix node to a clip without enhancementInfo"
      );
    }
    super(audioContext, clip, playlistTime, logger);
    this.enhancedGain = this.audioContext.createGain();
    this.originalGain = this.audioContext.createGain();
    this.backgroundGain = this.audioContext.createGain();
    this.isolatedSpeechGain = this.audioContext.createGain();
    this.mixNode = this.audioContext.createGain();
    this.splitter = this.audioContext.createChannelSplitter(4);
    this.merger = this.audioContext.createChannelMerger(2);
    this.splitter.channelInterpretation = "discrete";
    this.splitter.channelCountMode = "explicit";
    this.splitter.connect(this.originalGain, 0);
    this.splitter.connect(this.enhancedGain, 1);
    this.splitter.connect(this.backgroundGain, 2);
    this.splitter.connect(this.isolatedSpeechGain, 3);
    this.originalGain.connect(this.merger, 0, 0);
    this.originalGain.connect(this.merger, 0, 1);
    this.enhancedGain.connect(this.merger, 0, 0);
    this.enhancedGain.connect(this.merger, 0, 1);
    this.backgroundGain.connect(this.merger, 0, 0);
    this.backgroundGain.connect(this.merger, 0, 1);
    this.isolatedSpeechGain.connect(this.merger, 0, 0);
    this.isolatedSpeechGain.connect(this.merger, 0, 1);
    this.merger.connect(this.mixNode);
    this.hotswap(clip);
  }

  hotswap(newClip: PlaylistClip): void {
    if (newClip.enhancementInfo === undefined) {
      return;
    }

    const enhancementEnabled =
      newClip.enhancementInfo.isStudioSoundEnabled !== false;

    const bypassMixing = !enhancementEnabled;

    const gains = computeTwoSliderGains({
      mode: ModelVersion.v2,
      enhancePercent: newClip.enhancementInfo.strengthPercent,
      backgroundPercent: newClip.enhancementInfo.backgroundStrengthPercent || 0,
      bypassMixing,
    });

    this.logger.debug("🔄 EnhanceV2MixEffect hotswap", {
      clipId: newClip.id,
      strengthPercent: newClip.enhancementInfo.strengthPercent,
      backgroundStrengthPercent:
        newClip.enhancementInfo.backgroundStrengthPercent,
      isStudioSoundEnabled: newClip.enhancementInfo.isStudioSoundEnabled,
      enhancementEnabled,
      gains,
    });

    this.originalGain.gain.value = gains.originalGain;
    this.enhancedGain.gain.value = gains.enhancedSpeechGain;
    this.backgroundGain.gain.value = gains.backgroundGain;
    this.isolatedSpeechGain.gain.value = gains.isolatedSpeechGain;
  }

  get inputNode(): AudioNode {
    return this.splitter;
  }

  get outputNode(): AudioNode {
    return this.mixNode;
  }

  disconnect(): void {
    this.splitter.disconnect();
    this.enhancedGain.disconnect();
    this.originalGain.disconnect();
    this.backgroundGain.disconnect();
    this.isolatedSpeechGain.disconnect();
    this.merger.disconnect();
    this.mixNode.disconnect();
  }
}
